import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import type { ProgressType } from '@salescore/client-common'
import {
  CORE_CONSTANT,
  getInnerRowSpan,
  type SheetCell,
  type ViewQueryRecordNode,
  type ViewQueryRecordNodeWithId,
} from '@salescore/core'
import { HashMap, Option } from 'effect'
import type { ReactElement } from 'react'

import type { PivotColumn, PivotRowColumn, PivotSystemColumn } from '../../../domain/column'
import type { RSheetsCursorPosition } from '../../../domain/cursor/cursorTypes'
import type { KpiTableContext } from '../../../recoil/context.ts'
import type { LatestFieldChangeItem } from '../../../recoil/recordsHistory'
import type { RowConfig } from '../../../recoil/rowConfig'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms.ts'
import { DEFAULT_HEADER_COLOR } from '../../../rsheet/components/header/RSheetsNodeBlockHeader.tsx'
import type { SetMainCursorByRecordIdArguments } from '../../../rsheet/hooks/useSetMainCursorByRecordId.ts'
import { RSheetsStyle } from '../../../rsheet/util/RSheetsStyle.ts'
import { KpiRowCell } from './KpiRowCell.tsx'
import { KpiSystemCell } from './KpiSystemCell.tsx'
import { KpiValueCell } from './KpiValueCell.tsx'

export const KpiTableCellGroup = ({
  row,
  column,
  rowIndex,
  columnIndex,
  getInnerCells,
  expandable,
  expanded,
  latestFieldChangesRow,
  rowHeight,
  setDragging,
  setMainCursorByRecordId,
  setRowConfig,
  expandOnDrag,
  view,
  context,
  columns,
  rootRecordNode,
  progress,
  ability,
}: {
  row: ViewQueryRecordNodeWithId
  column:
    // eslint-disable-next-line sonarjs/use-type-alias
    PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  rowIndex: number
  columnIndex: number
  getInnerCells: (
    row: ViewQueryRecordNodeWithId,
    column:
      PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>,
  ) => SheetCell[]
  expandable: boolean
  expanded: boolean
  latestFieldChangesRow: HashMap.HashMap<string, LatestFieldChangeItem>
  rowHeight: number
  setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
  setRowConfig: (id: string, updater: (oldValue: RowConfig) => RowConfig) => void
  view: ViewFieldsFragment
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  context: KpiTableContext
  columns: Array<
    PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  >
  rootRecordNode: ViewQueryRecordNodeWithId
  progress: ProgressType
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const sheetCellGroup = getInnerCells(row, column)
  const height = getInnerRowSpan(row) * rowHeight
  const { width } = column
  const expandIconStyle = { fontSize: 10, color: RSheetsStyle.color.gray }

  return (
    <div
      className="relative"
      style={{
        width: column.width,
        transition: RSheetsStyle.cellTransition,
        backgroundColor: DEFAULT_HEADER_COLOR.cellColor,
      }}
    >
      {/* eslint-disable-next-line array-callback-return */}
      {sheetCellGroup.map((sheetCell, index) => {
        const { recordNode, parentRecordNode, height } = sheetCell
        if (parentRecordNode === undefined && recordNode === undefined) {
          return <BlankCell key={index} width={width} height={height * rowHeight} />
        }
        switch (column.columnType) {
          case 'pivot_system': {
            return (
              <KpiSystemCell
                key={index}
                sheetCell={sheetCell}
                rootRecordNode={rootRecordNode}
                column={column}
                rowIndex={rowIndex}
                expandable={expandable}
                expanded={expanded}
                rowHeight={rowHeight}
                columns={columns}
                setRowConfig={setRowConfig}
                view={view}
                ability={ability}
              />
            )
          }
          case 'pivot_row': {
            const changeKey: `${string},${string},${string}` = `${recordNode?.id},${
              column.node.write?.streamName ?? column.node.name // 目標シートの時は write.streamName が undefined になる
            },${column.field.name}`
            const fieldChange = HashMap.get(latestFieldChangesRow, changeKey).pipe(Option.getOrUndefined)
            const isFieldChanged = fieldChange !== undefined
            const isNewRecord = recordNode?.id?.startsWith(CORE_CONSTANT.VIEW_NEW_RECORD_PREFIX) ?? false

            return (
              <KpiRowCell
                key={index}
                sheetCell={sheetCell}
                rootRecordNode={rootRecordNode}
                column={column}
                rowIndex={rowIndex}
                columnIndex={columnIndex}
                isNewRecord={isNewRecord}
                isFieldChanged={isFieldChanged}
                overrideValue={fieldChange?.value}
                rowHeight={rowHeight}
                context={context}
                columns={columns}
                setMainCursorByRecordId={setMainCursorByRecordId}
                view={view}
                setDragging={setDragging}
                expandOnDrag={expandOnDrag}
                progress={progress}
                ability={ability}
              />
            )
          }
          case 'pivot': {
            const changeKey: `${string},${string},${string}` = `${recordNode?.id},${
              column.node.write?.streamName ?? column.node.name // 目標シートの時は write.streamName が undefined になる
            },${column.field.name}`
            const fieldChange = HashMap.get(latestFieldChangesRow, changeKey).pipe(Option.getOrUndefined)
            const isFieldChanged = fieldChange !== undefined
            const isNewRecord = recordNode?.id?.startsWith(CORE_CONSTANT.VIEW_NEW_RECORD_PREFIX) ?? false

            return (
              <KpiValueCell
                key={index}
                sheetCell={sheetCell}
                rootRecordNode={rootRecordNode}
                column={column}
                rowIndex={rowIndex}
                columnIndex={columnIndex}
                isNewRecord={isNewRecord}
                isFieldChanged={isFieldChanged}
                overrideValue={fieldChange?.value}
                rowHeight={rowHeight}
                context={context}
                columns={columns}
                setMainCursorByRecordId={setMainCursorByRecordId}
                view={view}
                setDragging={setDragging}
                expandOnDrag={expandOnDrag}
                progress={progress}
                ability={ability}
              />
            )
          }
        }
      })}
      {sheetCellGroup.length === 0 && <BlankCell width={width} height={height} />}
      {expandable && column.node.path.length > 1 && (
        <div
          className="absolute flex w-full items-center justify-center align-middle"
          style={{
            height: 1,
            width: '100%',
            bottom: 4,
          }}
          onClick={() => {
            setRowConfig(row.id, (x) => ({ ...x, expanded: !expanded }))
          }}
        >
          {expanded ? <CaretUpOutlined style={expandIconStyle} /> : <CaretDownOutlined style={expandIconStyle} />}
        </div>
      )}
    </div>
  )
}

const BlankCell = ({ width, height }: { width: number; height: number }): ReactElement => (
  <div
    style={{
      height,
      width,
      border: `1px ${RSheetsStyle.color.border} solid`,
      background: `linear-gradient(to bottom left, transparent calc(50% - 1px), ${RSheetsStyle.color.border} calc(50% - 0.5px), ${RSheetsStyle.color.border} calc(50% + 0.5px), transparent calc(50% + 1px))`,
    }}
  />
)
